import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import DashboardLoader from "./DashboardLoader";
import { FiSave } from "react-icons/fi";
import { apiRequest, apiFormRequest, getImageUrl } from "../../services/api";
import FeaturesManager from "./FeaturesManager";
import AboutManager from "./AboutManager";
import FooterManager from "./FooterManager";
import FileInputPreview from "../../ui/FileInputPreview";

const fetchPage = async (slug) => {
    const { data } = await apiRequest(`/pages/${slug}`);
    return data;
};

const updatePage = async ({ slug, formData }) => {
    const { data } = await apiFormRequest(`/pages/${slug}`, formData, {
        method: 'PUT' // apiFormRequest defaults to POST, so we override
    });
    return data;
};

const CMS_PAGES = [
    { id: "home", label: "Home Page" },
    { id: "about", label: "About Section" },
];

const SITE_SECTIONS = [
    { id: "features", label: "Features / Services" },
    { id: "footer", label: "Footer" },
];

const ALL_TABS = [...CMS_PAGES, ...SITE_SECTIONS];

const PagesManager = () => {
    const [selectedPage, setSelectedPage] = useState("home");
    const queryClient = useQueryClient();
    const { register, handleSubmit, reset, formState: { errors } } = useForm();

    // Fetch Data
    const { data: pageData, isLoading, isError } = useQuery({
        queryKey: ["page", selectedPage],
        queryFn: () => fetchPage(selectedPage),
        enabled: selectedPage !== 'features' && selectedPage !== 'footer' // Don't fetch generic page data for Features/Footer manager
    });

    // Populate form when data arrives
    useEffect(() => {
        if (pageData && pageData.content) {
            let content = pageData.content;
            // Handle double-stringified data recursively (legacy fix)
            while (typeof content === 'string') {
                try {
                    content = JSON.parse(content);
                } catch (e) {
                    console.error("Error parsing content JSON", e);
                    break;
                }
            }
            reset(content);
        } else if (selectedPage === 'about' && !pageData?.content) {
            // If about page has no content yet, reset cleanly
            reset({});
        }
    }, [pageData, reset, selectedPage]);

    // Parsed page content, available in render (not just the reset-form effect),
    // so file inputs can show a preview of the currently-saved image.
    const parsedContent = useMemo(() => {
        let content = pageData?.content;
        if (!content) return null;
        try {
            while (typeof content === "string") {
                content = JSON.parse(content);
            }
        } catch (e) {
            return null;
        }
        return content;
    }, [pageData]);

    const mutation = useMutation({
        mutationFn: updatePage,
        onSuccess: () => {
            queryClient.invalidateQueries(["page", selectedPage]);
            toast.success("Page updated successfully!");
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || "Failed to update page");
        }
    });

    const onSubmit = (data) => {
        const formData = new FormData();

        // Append text fields
        Object.keys(data).forEach(key => {
            if (key !== 'hero_image' && key !== 'resume' && key !== 'about_image') {
                formData.append(key, data[key]);
            }
        });

        // Append files
        if (data.hero_image && data.hero_image[0]) {
            formData.append("hero_image", data.hero_image[0]);
        }
        if (data.resume && data.resume[0]) {
            formData.append("resume", data.resume[0]);
        }
        if (data.about_image && data.about_image[0]) {
            formData.append("about_image", data.about_image[0]);
        }

        mutation.mutate({ slug: selectedPage, formData });
    };

    if (isLoading) return <DashboardLoader />;
    if (isError) return <div className="p-4 text-red-500">Error loading page data.</div>;

    return (
        <div className="w-full p-4  bg-white dark:bg-boxdark rounded-sm border border-stroke dark:border-strokedark shadow-default">

            {/* Header */}
            <div className="mb-6 pb-4 border-b border-stroke dark:border-strokedark">
                <h2 className="text-xl font-semibold text-black dark:text-white">
                    {ALL_TABS.find((tab) => tab.id === selectedPage)?.label}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedPage === "features" || selectedPage === "footer"
                        ? "Site content block"
                        : "CMS page content"}
                </p>
            </div>

            {/* Section navigation — grouped so it's clear these are two different
                kinds of content, not a flat list of "pages". */}
            <div className="mb-6 flex flex-wrap items-start gap-x-8 gap-y-4">
                <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">CMS Pages</p>
                    <div className="flex flex-wrap gap-2">
                        {CMS_PAGES.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setSelectedPage(tab.id)}
                                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${selectedPage === tab.id
                                    ? "bg-primary text-white shadow-md"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-boxdark-2 dark:text-gray-300 dark:hover:bg-opacity-80"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Site Sections</p>
                    <div className="flex flex-wrap gap-2">
                        {SITE_SECTIONS.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setSelectedPage(tab.id)}
                                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${selectedPage === tab.id
                                    ? "bg-primary text-white shadow-md"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-boxdark-2 dark:text-gray-300 dark:hover:bg-opacity-80"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {selectedPage === 'features' ? (
                <FeaturesManager />
            ) : selectedPage === 'footer' ? (
                <FooterManager />
            ) : (
                <form onSubmit={handleSubmit(onSubmit)}>
                    {selectedPage === 'home' && (
                        <div className="grid grid-cols-1 gap-6">
                            {/* Hero Section */}
                            <div className=" bg-gray-50 dark:bg-meta-4/30 p-4 rounded-lg">
                                <h3 className="text-lg font-medium mb-4 text-black dark:text-white">Hero Section</h3>

                                <div className="mb-4">
                                    <label className="mb-2.5 block font-medium text-black dark:text-white">Hero Title</label>
                                    <input
                                        type="text"
                                        {...register("hero_title")}
                                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">Subtitle 1</label>
                                        <input
                                            type="text"
                                            {...register("hero_subtitle_1")}
                                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">Subtitle 2</label>
                                        <input
                                            type="text"
                                            {...register("hero_subtitle_2")}
                                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">Subtitle 3</label>
                                        <input
                                            type="text"
                                            {...register("hero_subtitle_3")}
                                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">Subtitle 4</label>
                                        <input
                                            type="text"
                                            {...register("hero_subtitle_4")}
                                            className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input"
                                        />
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="mb-2.5 block font-medium text-black dark:text-white">Hero Description</label>
                                    <textarea
                                        rows={4}
                                        {...register("hero_description")}
                                        className="w-full rounded border-[1.5px] border-stroke bg-transparent py-3 px-5 font-medium outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                                    ></textarea>
                                </div>
                            </div>

                            {/* Files Section */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gray-50 dark:bg-meta-4/30 p-4 rounded-lg">
                                    <h3 className="text-lg font-medium mb-4 text-black dark:text-white">Hero Image</h3>
                                    <div className="mb-4">
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">Upload New Image</label>
                                        <FileInputPreview
                                            registerProps={register("hero_image")}
                                            existingImageUrl={parsedContent?.hero_image ? getImageUrl(parsedContent.hero_image) : null}
                                            previewClassName="mb-3 h-20 w-20 rounded-lg object-cover border border-stroke dark:border-form-strokedark"
                                            inputClassName="w-full cursor-pointer rounded-lg border-[1.5px] border-stroke bg-transparent font-medium outline-none transition file:mr-5 file:border-collapse file:cursor-pointer file:border-0 file:border-r file:border-solid file:border-stroke file:bg-whiter file:py-3 file:px-5 file:hover:bg-primary file:hover:bg-opacity-10 focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:file:border-form-strokedark dark:file:bg-white/30 dark:file:text-white dark:focus:border-primary"
                                        />
                                    </div>
                                </div>

                                <div className="bg-gray-50 dark:bg-meta-4/30 p-4 rounded-lg">
                                    <h3 className="text-lg font-medium mb-4 text-black dark:text-white">Resume</h3>
                                    <div className="mb-4">
                                        <label className="mb-2.5 block font-medium text-black dark:text-white">Upload New Resume (PDF)</label>
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            {...register("resume")}
                                            className="w-full cursor-pointer rounded-lg border-[1.5px] border-stroke bg-transparent font-medium outline-none transition file:mr-5 file:border-collapse file:cursor-pointer file:border-0 file:border-r file:border-solid file:border-stroke file:bg-whiter file:py-3 file:px-5 file:hover:bg-primary file:hover:bg-opacity-10 focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:file:border-form-strokedark dark:file:bg-white/30 dark:file:text-white dark:focus:border-primary"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedPage === 'about' && (
                        <AboutManager
                            register={register}
                            errors={errors}
                            existingImageUrl={parsedContent?.about_image ? getImageUrl(parsedContent.about_image) : null}
                        />
                    )}

                    <div className="mt-6">
                        <button
                            type="submit"
                            disabled={mutation.isLoading}
                            className="group relative flex w-full justify-center overflow-hidden rounded-lg bg-gradient-to-r from-primary to-secondary p-3 font-medium text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:translate-y-0 active:scale-95 disabled:opacity-50"
                        >
                            <span className="relative z-10 flex items-center gap-2">
                                {mutation.isLoading ? "Saving..." : "Save Changes"}
                                {!mutation.isLoading && <FiSave className="ml-2 mt-1" />}
                            </span>
                            <div className="absolute inset-0 z-0 bg-gradient-to-l from-primary to-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default PagesManager;
